import { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { getAuthErrorMessage } from "@/lib/authUtils";

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export function ForgotPasswordForm({ onBackToLogin }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
      setEmail(""); // Clear the email field
    } catch (error: any) {
      console.error("Password reset failed:", error);
      setError(getAuthErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {success ? (
        <div className="space-y-6">
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 space-grotesk">
              <strong className="font-semibold">Email sent!</strong>
              <br />
              Check your inbox for password reset instructions. If you don't see the email, check your spam folder.
            </AlertDescription>
          </Alert>
          
          <Button
            type="button"
            variant="ghost"
            onClick={onBackToLogin}
            className="w-full font-bold space-grotesk tracking-wide"
          >
            Back to Sign In
          </Button>
        </div>
      ) : (
        <>
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-2">
              <Mail className="h-6 w-6 text-orange-600" />
            </div>
            <p className="text-sm text-slate-600 space-grotesk">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-grotesk">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handlePasswordReset} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="reset-email" className="text-slate-700 font-medium space-grotesk">
                Email Address
              </Label>
              <Input
                id="reset-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
                className="h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all"
                placeholder="Enter your email address"
              />
            </div>

            <Button 
              type="submit" 
              variant="primary"
              size="primary"
              className="w-full h-12"
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Reset Instructions
            </Button>
          </form>

          <div className="text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={onBackToLogin}
              className="font-medium space-grotesk"
            >
              Back to Sign In
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

